import { GlobalBenefitsBadge } from '@/components/GlobalBenefitsBadge';
import { ProductDetails } from '@/components/ProductDetails';
import { QuotePriceDisplay } from '@/components/quote-cards/QuotePriceDisplay';
import { QuoteProductInfo } from '@/components/quote-cards/QuoteProductInfo';
import { QuoteROIDisplay } from '@/components/quote-cards/QuoteROIDisplay';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib';
import { Quote } from '@client/model';
import { getPolicyTypeFromProductName } from '@client/util/findPartnerPolicyFundingSourceId';
import { ChevronUp } from 'lucide-react';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  roiValues?: any;
  projectedCostCoverage?: number;
}

export interface LtcPlanningQuoteCardProps {
  quote: QuoteWithROI;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onSelectQuote: (quote: QuoteWithROI) => void;
  hasHighestROI?: boolean;
  isSelected?: boolean;
  testId?: string;
}

export function LtcPlanningQuoteCard({
  quote,
  isExpanded,
  onToggleExpand,
  onSelectQuote,
  hasHighestROI = false,
  isSelected = false,
  testId,
}: LtcPlanningQuoteCardProps) {
  const displayROI = quote.formattedROI;

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectQuote(quote);
  };

  return (
    <Card
      data-testid={testId}
      className={cn(
        'border-primary/20 hover:border-primary/40 w-full cursor-pointer rounded-xl p-5 transition-all duration-200 hover:shadow-xl hover:shadow-black/5',
        {
          'border-primary ring-primary/20 ring-2': isSelected,
          'border-primary/40 shadow-lg': hasHighestROI && !isSelected,
        },
      )}
      onClick={onToggleExpand}
    >
      <div className="relative">
        <div className="flex flex-wrap items-start gap-4">
          {/* Product Info */}
          <div className="flex flex-col gap-2">
            <QuoteProductInfo
              carrierName={quote.carrierName}
              productName={quote.productName}
              showLogo={true}
              showRating={true}
            />
            <div className="flex max-w-xs flex-wrap gap-2">
              <GlobalBenefitsBadge hasGlobalBenefits={quote.hasGlobalBenefits} />
            </div>
          </div>

          {/* ROI Display */}
          <QuoteROIDisplay
            projectedCostCoverage={quote.projectedCostCoverage}
            policyType={getPolicyTypeFromProductName(
              quote.productName,
              quote.policyType === 'Joint Life',
            )}
            displayROI={displayROI}
          />

          {/* Price Display */}
          <QuotePriceDisplay
            price={quote.premiumAmount}
            fundingOption={quote.productFundingOption}
          />

          {/* Select Button */}
          <div className="ml-auto flex flex-col gap-2">
            <Button
              type="button"
              className={cn('rounded-lg', {
                'bg-primary': !isSelected,
                'bg-green-600 hover:bg-green-700': isSelected,
              })}
              onClick={handleSelect}
            >
              {isSelected ? 'Selected' : 'Select Policy'}
            </Button>
          </div>
        </div>

        {/* Product Details Expand/Collapse */}
        <div className="border-primary/20 mt-4 border-t">
          <div
            data-testid="ltc-planning-quote-expand-button"
            className="flex cursor-pointer items-center justify-between pt-5"
            onClick={e => {
              e.stopPropagation();
              onToggleExpand();
            }}
          >
            <span className="sm:text-md text-primary-dark select-none text-sm font-medium">
              Product Details
            </span>
            <ChevronUp
              className={cn('text-primary-dark size-5 transition-transform duration-200', {
                'rotate-180': isExpanded,
              })}
            />
          </div>
        </div>

        {/* Expanded Product Details */}
        {isExpanded && <ProductDetails quote={quote} />}
      </div>
    </Card>
  );
}

